import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Engine } from "@babylonjs/core/Engines/engine";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Scene } from "@babylonjs/core/scene";

interface CameraPreset {
  readonly label: string;
  readonly position: Vector3;
  readonly target: Vector3;
}

const CAMERA_PRESETS: readonly CameraPreset[] = [
  { label: "入口全景", position: new Vector3(0, 1.65, -3.45), target: new Vector3(0, 1.05, 1.1) },
  { label: "左侧斜角", position: new Vector3(-3.75, 1.65, -2.35), target: new Vector3(1.15, 1, 1.15) },
  { label: "右侧斜角", position: new Vector3(3.75, 1.65, -1.75), target: new Vector3(-1.1, 1.05, 1.4) },
  { label: "窗边斜角", position: new Vector3(-3.7, 1.55, -0.6), target: new Vector3(0.2, 1.15, 3.4) },
  { label: "木地板视角", position: new Vector3(3.7, 1.38, -2.65), target: new Vector3(0.15, 0.38, 0.65) },
];

export class WoodLivingRoomPrototype {
  private readonly canvas: HTMLCanvasElement;
  private readonly engine: Engine;
  private readonly scene: Scene;
  private readonly camera: UniversalCamera;
  private activeView = 0;

  constructor(root: HTMLElement) {
    root.innerHTML = `
      <div class="prototype-shell">
        <canvas id="prototype-canvas" aria-label="木地板客厅三维原型"></canvas>
        <header class="prototype-header">
          <div><p>Stimulus Prototype 01</p><h1>木地板客厅</h1><span>同一3D场景 · 五个固定镜头 · Quest 3预算原型</span></div>
          <a href="./">返回训练</a>
        </header>
        <nav class="prototype-camera-bar" aria-label="镜头角度">
          ${CAMERA_PRESETS.map((preset, index) => `<button type="button" data-view="${index}">${index + 1}. ${preset.label}</button>`).join("")}
          <button id="download-prototype" type="button">下载当前PNG</button>
        </nav>
        <aside class="prototype-note">当前为实时低多边形视觉原型；正式图片将从同一模型按统一16:9规格导出。</aside>
      </div>`;
    const canvas = root.querySelector("#prototype-canvas");
    if (!(canvas instanceof HTMLCanvasElement)) throw new Error("Missing prototype canvas");
    this.canvas = canvas;
    root.classList.toggle("prototype-render-only", new URLSearchParams(location.search).get("render") === "1");
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
    this.scene = new Scene(this.engine);
    this.scene.clearColor = Color4.FromHexString("#c7d0d4ff");
    this.scene.ambientColor = Color3.FromHexString("#6c7375");
    this.camera = new UniversalCamera("prototype-camera", CAMERA_PRESETS[0]?.position.clone() ?? Vector3.Zero(), this.scene);
    this.camera.fov = 0.93;
    this.camera.minZ = 0.05;
    this.scene.activeCamera = this.camera;
    this.buildScene();
    root.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
      button.addEventListener("click", () => this.setView(Number(button.dataset.view ?? 0)));
    });
    root.querySelector<HTMLButtonElement>("#download-prototype")?.addEventListener("click", () => this.downloadCurrentView());
    window.addEventListener("resize", () => this.engine.resize());
    const requested = Math.max(0, Math.min(4, Number(new URLSearchParams(location.search).get("view") ?? 1) - 1));
    this.setView(requested);
    this.engine.runRenderLoop(() => this.scene.render());
  }

  private setView(index: number): void {
    const preset = CAMERA_PRESETS[index];
    if (!preset) return;
    this.activeView = index;
    this.camera.position.copyFrom(preset.position);
    this.camera.setTarget(preset.target);
    document.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
      button.classList.toggle("active", Number(button.dataset.view) === index);
    });
  }

  private buildScene(): void {
    new HemisphericLight("soft-fill", new Vector3(-0.2, 1, -0.15), this.scene).intensity = 0.72;
    const sun = new DirectionalLight("window-daylight", new Vector3(-0.35, -0.8, -0.5), this.scene);
    sun.position.set(2.4, 4.5, 6);
    sun.intensity = 1.15;
    const lampLight = new PointLight("lamp-light", new Vector3(3.65, 2.25, 1.95), this.scene);
    lampLight.diffuse = Color3.FromHexString("#ffd5a0");
    lampLight.intensity = 0.34;
    lampLight.range = 4.5;

    const plaster = this.material("warm-plaster", "#e5e0d6");
    const ceilingMaterial = this.material("ceiling", "#d8d5ce");
    ceilingMaterial.backFaceCulling = false;
    ceilingMaterial.emissiveColor = Color3.FromHexString("#242321");
    const oak = [this.material("oak-mid", "#96633a"), this.material("oak-light", "#a97749"), this.material("oak-dark", "#784c2d")] as const;
    const trim = this.material("wood-trim", "#60432e");
    const sofa = this.material("sofa-fabric", "#6f7c80");
    const blue = this.material("muted-blue", "#60798a");
    const metal = this.material("dark-metal", "#292f32");
    const rug = this.material("woven-rug", "#84939a");
    const ceramic = this.material("ceramic", "#c8b99f");
    const leaf = this.material("plant-leaf", "#496c50");
    const glass = this.material("window-glass", "#9fc8d6", 0.52);
    glass.emissiveColor = Color3.FromHexString("#a8d4e3").scale(0.52);

    this.box("floor-base", 10, 0.12, 8, new Vector3(0, -0.1, 0), oak[2]);
    for (let row = 0; row < 16; row += 1) {
      for (let column = 0; column < 8; column += 1) {
        let x = -4.35 + column * 1.24 + (row % 2) * 0.62;
        if (x > 4.7) x -= 9.92;
        this.box(`plank-${row}-${column}`, 1.2, 0.035, 0.465, new Vector3(x, -0.02, -3.48 + row * 0.47), oak[(row * 3 + column * 5) % 3] ?? oak[0]);
      }
    }
    this.box("wall-left", 0.16, 3.1, 8, new Vector3(-4.95, 1.55, 0), plaster);
    this.box("wall-right", 0.16, 3.1, 8, new Vector3(4.95, 1.55, 0), plaster);
    this.box("wall-back-left", 3.25, 3.1, 0.16, new Vector3(-3.38, 1.55, 3.95), plaster);
    this.box("wall-back-right", 3.25, 3.1, 0.16, new Vector3(3.38, 1.55, 3.95), plaster);
    this.box("wall-back-top", 3.5, 0.65, 0.16, new Vector3(0, 2.78, 3.95), plaster);
    this.box("wall-back-bottom", 3.5, 0.55, 0.16, new Vector3(0, 0.275, 3.95), plaster);
    this.box("ceiling", 10, 0.12, 8, new Vector3(0, 3.08, 0), ceilingMaterial);
    this.box("window", 3.45, 1.9, 0.05, new Vector3(0, 1.64, 3.84), glass);
    this.frameWindow(trim);

    this.box("rug", 4.2, 0.025, 2.75, new Vector3(0.3, 0.025, 0.3), rug);
    this.box("sofa-seat", 3.25, 0.48, 0.9, new Vector3(1.35, 0.46, 1.65), sofa);
    this.box("sofa-back", 3.25, 1.15, 0.32, new Vector3(1.35, 1.05, 2.03), sofa);
    this.box("sofa-arm-left", 0.34, 0.72, 1.02, new Vector3(-0.28, 0.62, 1.64), sofa);
    this.box("sofa-arm-right", 0.34, 0.72, 1.02, new Vector3(2.98, 0.62, 1.64), sofa);
    this.box("cushion-left", 0.68, 0.55, 0.2, new Vector3(0.55, 1.03, 1.78), blue).rotation.z = -0.08;
    this.box("cushion-right", 0.68, 0.55, 0.2, new Vector3(2.15, 1.03, 1.78), ceramic).rotation.z = 0.08;
    this.table(trim, oak[2], ceramic);
    this.bookcase(trim, plaster, [blue, ceramic, oak[0], leaf]);
    this.floorLamp(metal, ceramic);
    this.plant(ceramic, leaf);
    this.wallArt(trim, blue, this.material("art-ochre", "#b78652"));
  }

  private frameWindow(material: StandardMaterial): void {
    this.box("window-top", 3.65, 0.09, 0.11, new Vector3(0, 2.62, 3.76), material);
    this.box("window-bottom", 3.65, 0.09, 0.11, new Vector3(0, 0.67, 3.76), material);
    this.box("window-left", 0.09, 2.02, 0.11, new Vector3(-1.78, 1.64, 3.76), material);
    this.box("window-right", 0.09, 2.02, 0.11, new Vector3(1.78, 1.64, 3.76), material);
    this.box("window-mid", 0.07, 1.94, 0.1, new Vector3(0, 1.64, 3.75), material);
  }

  private table(wood: StandardMaterial, top: StandardMaterial, ceramic: StandardMaterial): void {
    this.box("coffee-top", 2.25, 0.12, 1.05, new Vector3(0.35, 0.48, -0.15), top);
    for (const [x, z] of [[-0.62, -0.52], [1.32, -0.52], [-0.62, 0.22], [1.32, 0.22]] as const) this.box(`coffee-leg-${x}-${z}`, 0.1, 0.48, 0.1, new Vector3(x, 0.24, z), wood);
    const vase = MeshBuilder.CreateCylinder("table-vase", { diameter: 0.24, height: 0.38, tessellation: 18 }, this.scene);
    vase.position.set(0.35, 0.73, -0.15);
    vase.material = ceramic;
  }

  private bookcase(wood: StandardMaterial, back: StandardMaterial, colors: readonly StandardMaterial[]): void {
    this.box("bookcase-body", 1.45, 2.35, 0.42, new Vector3(-4.54, 1.2, 1.85), wood);
    this.box("bookcase-inner", 1.18, 2.1, 0.45, new Vector3(-4.3, 1.2, 1.85), back);
    for (let shelf = 0; shelf < 4; shelf += 1) {
      this.box(`shelf-${shelf}`, 1.25, 0.08, 0.48, new Vector3(-4.3, 0.32 + shelf * 0.55, 1.85), wood);
      for (let book = 0; book < 4; book += 1) this.box(`book-${shelf}-${book}`, 0.14, 0.32 + ((book + shelf) % 2) * 0.08, 0.22, new Vector3(-4.7 + book * 0.27, 0.53 + shelf * 0.55, 1.77), colors[(shelf + book) % colors.length] ?? back);
    }
  }

  private floorLamp(metal: StandardMaterial, shadeMaterial: StandardMaterial): void {
    const pole = MeshBuilder.CreateCylinder("lamp-pole", { diameter: 0.065, height: 1.75, tessellation: 12 }, this.scene);
    pole.position.set(3.72, 0.93, 1.92); pole.material = metal;
    const base = MeshBuilder.CreateCylinder("lamp-base", { diameter: 0.55, height: 0.08, tessellation: 20 }, this.scene);
    base.position.set(3.72, 0.04, 1.92); base.material = metal;
    const shade = MeshBuilder.CreateCylinder("lamp-shade", { diameterTop: 0.35, diameterBottom: 0.72, height: 0.58, tessellation: 24 }, this.scene);
    shade.position.set(3.72, 1.98, 1.92); shade.material = shadeMaterial;
  }

  private plant(potMaterial: StandardMaterial, leafMaterial: StandardMaterial): void {
    const pot = MeshBuilder.CreateCylinder("plant-pot", { diameterTop: 0.48, diameterBottom: 0.36, height: 0.55, tessellation: 18 }, this.scene);
    pot.position.set(-3.72, 0.275, 3.15); pot.material = potMaterial;
    for (let index = 0; index < 7; index += 1) {
      const stem = MeshBuilder.CreateCylinder(`stem-${index}`, { diameter: 0.035, height: 0.8 + index * 0.08, tessellation: 8 }, this.scene);
      stem.position.set(-3.72 + (index - 3) * 0.06, 0.82 + index * 0.04, 3.15); stem.rotation.z = (index - 3) * 0.07; stem.material = leafMaterial;
      const crown = MeshBuilder.CreateSphere(`leaf-${index}`, { diameter: 0.34, segments: 8 }, this.scene);
      crown.scaling.set(0.7, 1.5, 0.45); crown.position.set(stem.position.x + (index - 3) * 0.05, 1.2 + index * 0.07, 3.15); crown.material = leafMaterial;
    }
  }

  private wallArt(frame: StandardMaterial, base: StandardMaterial, accent: StandardMaterial): void {
    this.box("art-frame", 1.55, 1.05, 0.08, new Vector3(4.85, 1.82, -0.15), frame).rotation.y = Math.PI / 2;
    this.box("art-canvas", 1.35, 0.85, 0.04, new Vector3(4.79, 1.82, -0.15), base).rotation.y = Math.PI / 2;
    this.box("art-block", 0.42, 0.58, 0.025, new Vector3(4.76, 1.75, 0.08), accent).rotation.y = Math.PI / 2;
  }

  private box(name: string, width: number, height: number, depth: number, position: Vector3, material: StandardMaterial): Mesh {
    const mesh = MeshBuilder.CreateBox(name, { width, height, depth }, this.scene);
    mesh.position.copyFrom(position); mesh.material = material; return mesh;
  }

  private material(name: string, hex: string, alpha = 1): StandardMaterial {
    const material = new StandardMaterial(name, this.scene);
    material.diffuseColor = Color3.FromHexString(hex); material.specularColor = Color3.FromHexString("#252525"); material.alpha = alpha;
    return material;
  }

  private downloadCurrentView(): void {
    this.scene.render();
    const link = document.createElement("a");
    link.download = `wood-living-room-view-${this.activeView + 1}.png`;
    link.href = this.canvas.toDataURL("image/png");
    link.click();
  }
}
